import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { buildUrl } from '@utils/url/url.util';
import { Constants } from '@utils/Constants';
import { ComponentService } from '@services/component.service';
import { RESTServerRoute } from '../../types/Server';
import { OCPIResponse } from '../../types/ocpi/OCPIResponse';
import {
  ActionResponse,
  OcpiEndpointDataResult,
  OCPIGenerateLocalTokenResponse,
  OCPIJobStatusesResponse,
  OCPIPingResponse,
  Ordering,
  Paging,
} from '../../types/DataResult';
import { FilterParams } from '../../types/GlobalType';
import { buildSortingParams } from '../utils/build-sorting-params.utils';
import { buildPagingParams } from '../utils/build-paging-params.utils';
import { OCPIEndpoint } from '../../types/ocpi/OCPIEndpoint';
import { OCPIRole } from '../../types/ocpi/OCPIRole';

@Injectable()
export class OcpiApiService {
  public rerouteToOCPI: boolean;
  public constructor(
    private httpClient: HttpClient,
    protected componentService: ComponentService,
  ) {
    this.rerouteToOCPI = this.componentService.isFeatureActive('REROUTE_OCPI_HANDSHAKE');
  }

  public getOcpiEndpoints(
    params: FilterParams,
    paging: Paging = Constants.DEFAULT_PAGING,
    ordering: Ordering[] = [],
  ): Observable<OcpiEndpointDataResult> {
    const queryParams = { ...params, ...buildSortingParams(ordering), ...buildPagingParams(paging) };

    return this.httpClient.get<OcpiEndpointDataResult>(RESTServerRoute.REST_OCPI_ENDPOINTS, {
      params: queryParams,
    });
  }

  public pingOcpiEndpoint(ocpiEndpoint: OCPIEndpoint): Observable<OCPIPingResponse> {
    return this.httpClient.put<OCPIPingResponse>(
      buildUrl(RESTServerRoute.REST_OCPI_ENDPOINT_PING, { id: ocpiEndpoint.id }),
      ocpiEndpoint,
    );
  }

  public createOcpiEndpoint(ocpiEndpoint: any): Observable<ActionResponse> {
    return this.httpClient.post<ActionResponse>(RESTServerRoute.REST_OCPI_ENDPOINTS, ocpiEndpoint);
  }

  public generateEmspToken(): Observable<OCPIResponse> {
    return this.httpClient.get<OCPIResponse>(RESTServerRoute.VEV_OCPI_GENERATE_LOCAL_TOKEN_EMSP);
  }

  public generateCpoToken(endpointName: string): Observable<OCPIGenerateLocalTokenResponse> {
    return this.httpClient.put<OCPIGenerateLocalTokenResponse>(
      RESTServerRoute.REST_OCPI_SERVER_ENDPOINT_GENERATE_LOCAL_TOKEN,
      { name: endpointName },
    );
  }

  public updateOcpiEndpoint(ocpiEndpoint: OCPIEndpoint): Observable<void> {
    const route = this.rerouteToOCPI ? RESTServerRoute.VEV_OCPI_ENDPOINT : RESTServerRoute.REST_OCPI_SERVER_ENDPOINT;
    return this.httpClient.put<void>(buildUrl(route, { id: ocpiEndpoint.id }), ocpiEndpoint);
  }

  public registerEndpoint(endpointId: string, role: OCPIRole): Observable<void> {
    if (role === OCPIRole.EMSP) {
      return this.httpClient.put<void>(buildUrl(RESTServerRoute.VEV_OCPI_REGISTER_ENDPOINT, { id: endpointId }), {});
    }
    const route = this.rerouteToOCPI
      ? RESTServerRoute.VEV_OCPI_REGISTER_ENDPOINT
      : RESTServerRoute.REST_OCPI_SERVER_REGISTER_ENDPOINT;
    return this.httpClient.put<void>(route.replace(':id', endpointId), {});
  }

  public unregisterOcpiEndpoint(id: string, role: OCPIRole): Observable<void> {
    if (role === OCPIRole.EMSP) {
      return this.httpClient.put<void>(buildUrl(RESTServerRoute.VEV_OCPI_UNREGISTER_ENDPOINT, { id }), {});
    }
    const route = this.rerouteToOCPI
      ? RESTServerRoute.VEV_OCPI_UNREGISTER_ENDPOINT
      : RESTServerRoute.REST_OCPI_SERVER_UNREGISTER_ENDPOINT;
    return this.httpClient.put<void>(buildUrl(route, { id }), {});
  }

  public updateEndpointCredentials(endpointId: string, role: OCPIRole): Observable<void> {
    // check if we are emsp or cpo
    if (role === OCPIRole.EMSP) {
      return this.httpClient.put<void>(buildUrl(RESTServerRoute.VEV_OCPI_UPDATE_CREDENTIALS, { id: endpointId }), {});
    }
    const route = this.rerouteToOCPI
      ? RESTServerRoute.VEV_OCPI_UPDATE_CREDENTIALS
      : RESTServerRoute.REST_OCPI_SERVER_UPDATE_CREDENTIALS;
    return this.httpClient.put<void>(buildUrl(route, { id: endpointId }), {});
  }

  public deleteEndpoint(endpointId: string): Observable<void> {
    const route = this.rerouteToOCPI ? RESTServerRoute.VEV_OCPI_ENDPOINT : RESTServerRoute.REST_OCPI_SERVER_ENDPOINT;
    return this.httpClient.delete<void>(buildUrl(route, { id: endpointId }));
  }

  public pushTokens(endpointId: string): Observable<void> {
    return this.httpClient.post<void>(
      buildUrl(RESTServerRoute.REST_OCPI_ENDPOINT_SEND_TOKENS_EMSP, { id: endpointId }),
      {},
    );
  }

  public fetchLocations(endpointId: string): Observable<void> {
    return this.httpClient.post<void>(buildUrl(RESTServerRoute.VEV_OCPI_FETCH_LOCATIONS, { id: endpointId }), {});
  }

  public pullSessionsOcpiEndpoint(endpointId: string): Observable<OCPIJobStatusesResponse> {
    return this.httpClient.put<OCPIJobStatusesResponse>(
      buildUrl(RESTServerRoute.REST_OCPI_ENDPOINT_PULL_SESSIONS, { id: endpointId }),
      {},
    );
  }

  public pullCdrsOcpiEndpoint(endpointId: string): Observable<OCPIJobStatusesResponse> {
    return this.httpClient.put<OCPIJobStatusesResponse>(
      buildUrl(RESTServerRoute.REST_OCPI_ENDPOINT_PULL_CDRS, { id: endpointId }),
      {},
    );
  }

  // CPO SIDE
  public sendEVSEStatusesOcpiEndpoint(endpointId: string): Observable<OCPIJobStatusesResponse> {
    return this.httpClient.put<OCPIJobStatusesResponse>(
      buildUrl(RESTServerRoute.REST_OCPI_ENDPOINT_SEND_EVSE_STATUSES, { id: endpointId }),
      {},
    );
  }

  public pullTokensOcpiEndpoint(endpointId: string): Observable<OCPIJobStatusesResponse> {
    return this.httpClient.put<OCPIJobStatusesResponse>(
      buildUrl(RESTServerRoute.REST_OCPI_ENDPOINT_PULL_TOKENS, { id: endpointId }),
      {},
    );
  }

  public checkLocationsOcpiEndpoint(endpointId: string): Observable<OCPIJobStatusesResponse> {
    return this.httpClient.put<OCPIJobStatusesResponse>(
      buildUrl(RESTServerRoute.REST_OCPI_SERVER_CHECK_LOCATIONS, { id: endpointId }),
      {},
    );
  }

  public checkSessionsOcpiEndpoint(endpointId: string): Observable<OCPIJobStatusesResponse> {
    return this.httpClient.put<OCPIJobStatusesResponse>(
      buildUrl(RESTServerRoute.REST_OCPI_SERVER_CHECK_SESSIONS, { id: endpointId }),
      {},
    );
  }

  public checkCdrsOcpiEndpoint(endpointId: string): Observable<OCPIJobStatusesResponse> {
    return this.httpClient.put<OCPIJobStatusesResponse>(
      buildUrl(RESTServerRoute.REST_OCPI_SERVER_CHECK_CDRS, { id: endpointId }),
      {},
    );
  }

  public pushPeriodDataOcpiEndpoint(endpointId: string): Observable<OCPIJobStatusesResponse> {
    // Execute
    return this.httpClient.put<OCPIJobStatusesResponse>(
      buildUrl(RESTServerRoute.REST_OCPI_ENDPOINT_PUSH_PERIOD_DATA, { id: endpointId }),
      {},
    );
  }
}
